import { createSlice } from '@reduxjs/toolkit';

const calendarDataSelfSlice = createSlice({
  name: 'calendarDataSelf',
  initialState: {
    data: {},              // Calendar data: { 'YYYY-MM-DD': { events, ovulationTests, ipData, sharedOvulationPredictions, note } }
    dataProcessed: {},     // Processed calendar data with periodStart and cd computed
    range_start: null,     // Earliest cached date (YYYY-MM-DD)
    range_end: null,       // Latest cached date (YYYY-MM-DD)
    last_updated: null,    // Timestamp of last Supabase fetch
    predictionUpdateCounter: 0, // Counter for period/ovulation test changes to trigger DB updates
  },
  reducers: {
    setCalendarDataSelf: (state, action) => {
      const { data, range_start, range_end } = action.payload;
      // Merge new data into existing instead of replacing,
      // so optimistic updates (e.g. contact removal) aren't wiped out
      // by a background Supabase reload completing after the optimistic update.
      state.data = { ...state.data, ...data };
      // Expand range to cover both old and new
      if (range_start) {
        state.range_start = state.range_start
          ? (range_start < state.range_start ? range_start : state.range_start)
          : range_start;
      }
      if (range_end) {
        state.range_end = state.range_end
          ? (range_end > state.range_end ? range_end : state.range_end)
          : range_end;
      }
      state.last_updated = Date.now();
    },
    setCalendarDataSelfProcessed: (state, action) => {
      state.dataProcessed = action.payload;
    },
    updateCalendarDataSelfDate: (state, action) => {
      const { date, data } = action.payload;
      state.data[date] = data;
    },
    incrementPredictionUpdateCounter: (state) => {
      state.predictionUpdateCounter += 1;
    },
    addOvulationTestToSelf: (state, action) => {
      const { test } = action.payload;
      // Convert UTC to local date string for key lookup
      const localDate = new Date(test.taken_at);
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;

      // Initialize day entry if it doesn't exist
      if (!state.data[dateKey]) {
        state.data[dateKey] = {};
      }

      // Initialize ovulationTests array if it doesn't exist
      if (!state.data[dateKey].ovulationTests) {
        state.data[dateKey].ovulationTests = [];
      }

      // Check if test already exists (by ID) and update it, otherwise add new
      const existingIndex = state.data[dateKey].ovulationTests.findIndex(t => t.id === test.id);
      if (existingIndex >= 0) {
        // Update existing test
        state.data[dateKey].ovulationTests[existingIndex] = test;
      } else {
        // Add new test
        state.data[dateKey].ovulationTests.push(test);
      }

      // Update maxTestValue for this day
      const allValues = state.data[dateKey].ovulationTests
        .map(t => t.value)
        .filter(v => v !== null && v !== undefined);
      if (allValues.length > 0) {
        state.data[dateKey].maxTestValue = Math.max(...allValues);
      }

      // Increment prediction update counter to trigger recalculation
      state.predictionUpdateCounter += 1;
    },
  },
});

export const { setCalendarDataSelf, setCalendarDataSelfProcessed, updateCalendarDataSelfDate, incrementPredictionUpdateCounter, addOvulationTestToSelf } = calendarDataSelfSlice.actions;
export default calendarDataSelfSlice.reducer;
